import React, { useEffect, useMemo, useState } from 'react';
import { hasValidReportAccessToken, hasViewerUnlock, unlockReportViewer } from './accessControl';
import { subscribeEmailToKlaviyo } from './klaviyo';
import MobilePdfViewer from './MobilePdfViewer';

const ProtectedReportViewer: React.FC = () => {
  const pdfUrl = process.env.REACT_APP_REPORT_PDF_URL || '/reports/mining-super-cycle-report.pdf';

  const accessToken = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('access') || '';
  }, []);

  const [isAuthorized, setIsAuthorized] = useState(() => {
    return hasValidReportAccessToken(accessToken) || hasViewerUnlock();
  });
  const [isMobile, setIsMobile] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 768px)');
    const update = () => setIsMobile(mql.matches);
    update();
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    const stopDefault = (event: Event) => {
      event.preventDefault();
    };

    const stopKeys = (event: KeyboardEvent) => {
      const key = event.key.toLowerCase();
      const blockedCombo = (event.ctrlKey || event.metaKey) && ['c', 'p', 's', 'u'].includes(key);
      const isPrintScreen = event.key === 'PrintScreen';

      if (blockedCombo || isPrintScreen) {
        event.preventDefault();
      }
    };

    const blockPrint = () => {
      window.stop();
    };

    const previousUserSelect = document.body.style.userSelect;
    document.body.style.userSelect = 'none';

    document.addEventListener('contextmenu', stopDefault);
    document.addEventListener('copy', stopDefault);
    document.addEventListener('cut', stopDefault);
    document.addEventListener('selectstart', stopDefault);
    document.addEventListener('dragstart', stopDefault);
    document.addEventListener('keydown', stopKeys);
    window.addEventListener('beforeprint', blockPrint);

    return () => {
      document.body.style.userSelect = previousUserSelect;
      document.removeEventListener('contextmenu', stopDefault);
      document.removeEventListener('copy', stopDefault);
      document.removeEventListener('cut', stopDefault);
      document.removeEventListener('selectstart', stopDefault);
      document.removeEventListener('dragstart', stopDefault);
      document.removeEventListener('keydown', stopKeys);
      window.removeEventListener('beforeprint', blockPrint);
    };
  }, []);

  const submitEmailGate = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');

    if (!/\S+@\S+\.\S+/.test(email)) {
      setError('Enter a valid email address.');
      return;
    }

    setIsSubmitting(true);
    try {
      await subscribeEmailToKlaviyo(email);
      unlockReportViewer();
      setIsAuthorized(true);
    } catch (requestError) {
      setError(
        requestError instanceof Error
          ? `Could not verify email right now: ${requestError.message}`
          : 'Could not verify email right now.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isAuthorized) {
    return (
      <div className="min-h-screen bg-[#0f1a31] text-white flex items-center justify-center px-6">
        <div className="w-full max-w-[520px] bg-[#14213f] border border-white/15 rounded-[12px] p-8 text-center">
          <h1 className="text-[28px] font-[700] leading-tight">Enter your email to access this report</h1>
          <p className="mt-2 text-white/80 text-[15px]">This report link cannot be shared directly without verification.</p>

          <form className="mt-6" onSubmit={submitEmailGate}>
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              placeholder="you@example.com"
              className="w-full h-[48px] rounded-full text-[#111827] px-5 text-center border border-white/20 focus:outline-none focus:ring-2 focus:ring-[#c49a2d]"
              required
            />
            <button
              type="submit"
              disabled={isSubmitting}
              className="mt-4 w-full h-[48px] rounded-[8px] bg-[#c49a2d] font-[700] text-[18px] disabled:bg-gray-500"
            >
              {isSubmitting ? 'Verifying...' : 'Continue to Report'}
            </button>
          </form>

          {error && <p className="mt-3 text-[13px] text-red-300">{error}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full overflow-hidden bg-[#0f1a31] select-none">
      <div className="absolute inset-0 pointer-events-none opacity-10 z-20 [background:repeating-linear-gradient(-30deg,transparent,transparent_220px,rgba(255,255,255,0.2)_220px,rgba(255,255,255,0.2)_260px)]" />
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 text-[11px] text-white/80 tracking-wide bg-black/30 px-3 py-1 rounded-full">
        Protected viewer mode
      </div>

      {/* Mobile: PDF.js viewer (fit-to-width). Desktop: iframe */}
      <div
        className="relative z-10 w-full border-0 h-screen"
        style={{
          height: '100dvh',
          minHeight: '-webkit-fill-available',
        }}
      >
        {isMobile ? (
          <MobilePdfViewer pdfUrl={pdfUrl} />
        ) : (
          <iframe
            title="Mining Super-Cycle Report"
            src={`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=0`}
            className="w-full h-full border-0 block"
            style={{ minHeight: '100%' }}
          />
        )}
      </div>
    </div>
  );
};

export default ProtectedReportViewer;
